"use client";

import * as React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────────
 * LendingTypeSelect — pill-based segmented control.
 *
 * Three options surface as pills:
 *   • HELOC — active (existing simulator math)
 *   • PLOC — disabled, "Coming soon" tooltip on hover/focus
 *   • Margin — disabled, "Coming soon" tooltip on hover/focus
 *
 * Slice 11 ships the pill shell; Slice 12 wires up the other two by
 * flipping their `available` flag and adding their default spreads.
 *
 * Same visual register as the WindowPresetSelect — segmented buttons with
 * a subtle active fill. Inactive pills get the muted treatment plus a
 * cursor-not-allowed and aria-disabled.
 * ────────────────────────────────────────────────────────────────────────── */

export type LendingType = "heloc" | "ploc" | "margin";

interface LendingTypeOption {
  value: LendingType;
  label: string;
  available: boolean;
}

export const LENDING_TYPES: readonly LendingTypeOption[] = [
  { value: "heloc", label: "HELOC", available: true },
  { value: "ploc", label: "PLOC", available: false },
  { value: "margin", label: "Margin", available: false },
] as const;

export interface LendingTypeSelectProps {
  value: LendingType;
  onChange: (value: LendingType) => void;
  disabled?: boolean;
}

export function LendingTypeSelect({ value, onChange, disabled }: LendingTypeSelectProps) {
  return (
    <TooltipProvider delayDuration={150}>
      <div
        role="group"
        aria-label="Lending type"
        className="border-input bg-background flex h-9 gap-1 rounded-md border p-0.5"
      >
        {LENDING_TYPES.map((opt) => {
          const active = value === opt.value;
          const interactive = opt.available && !disabled;
          const button = (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              aria-disabled={!interactive}
              disabled={!interactive}
              onClick={() => {
                if (interactive) onChange(opt.value);
              }}
              className={cn(
                "flex-1 rounded-sm px-2 text-xs font-medium tracking-tight transition-colors",
                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                active && interactive
                  ? "bg-stone-200/80 text-zinc-900 shadow-sm"
                  : interactive
                    ? "text-zinc-600 hover:bg-stone-100 hover:text-zinc-900"
                    : "text-zinc-400 disabled:pointer-events-auto",
              )}
            >
              {opt.label}
            </button>
          );

          if (opt.available) return button;
          return (
            <Tooltip key={opt.value}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent>Coming soon</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
